// File: src/components/pages/BookYourSlots.jsx
// UPDATED: Added booking functionality with BOOK NOW action

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, ChevronRight, Clock, Coffee, AlertCircle, CheckCircle, Sparkles } from 'lucide-react';
import { bookingSlotAPI,bookingAPI } from '../../services/api';
import { getMondayOfCurrentWeek } from '../../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK } from '../../utils/constants';
import '../../styles/unified.css';
import { toast } from '../../utils/toast';

export default function BookYourSlots() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceUser = userRoles.includes('ServiceUser');
  const userAuth0Id = user?.sub;

  const [publishedTemplate, setPublishedTemplate] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMondayOfCurrentWeek());
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBooking, setIsBooking] = useState(false);

  const [existingBookings, setExistingBookings] = useState(new Set());

  useEffect(() => {
    if (isServiceUser && userAuth0Id) {
      loadInstructorSlotsFromRelationship();
    }
  }, [isServiceUser, userAuth0Id]);

  const loadInstructorSlotsFromRelationship = async () => {
  setIsLoading(true);
  setError(null);
  
    try {
      const response = await bookingSlotAPI.getMyInstructorSlots(getAccessTokenSilently);
      
      if (response.success && response.data) {
        setPublishedTemplate(response.data);
        
        // NEW: Load existing bookings for this instructor
        await loadExistingBookings(response.data.spPhoneId);
      } else {
        setPublishedTemplate(null);
        setError('Contact your Service Provider / Instructor to book a slot.');
      }
    } catch (err) {
      console.error('Failed to load instructor slots:', err);
      setPublishedTemplate(null);
      setError('Contact your Service Provider / Instructor to book a slot.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
 * Load existing bookings for the instructor to disable already-booked slots
 */
  const loadExistingBookings = async (instructorPhone) => {
    try {
      console.log('Loading existing bookings for instructor:', instructorPhone);
      
      const response = await bookingAPI.getBookingsByInstructorPhone(
        instructorPhone,
        getAccessTokenSilently
      );
      
      if (response.success && response.data) {
        // Create a Set of booked slot keys (date-startTime-endTime)
        const bookedSlots = new Set();
        
        response.data.forEach(booking => {
          // Only include active bookings (not cancelled)
          if (booking.status !== 'cancelled') {
            const slotKey = `${booking.date}-${booking.startTime}-${booking.endTime}`;
            bookedSlots.add(slotKey);
          }
        });
        
        console.log(`Found ${bookedSlots.size} existing bookings`);
        setExistingBookings(bookedSlots);
      }
    } catch (err) {
      console.error('Failed to load existing bookings:', err);
      // Don't block the UI if this fails - just log the error
    }
  };

  const handleBookNow = async () => {
    if (selectedSlots.length === 0) return;

    setIsBooking(true);


    try {
      // Prepare slots data for booking
      const slots = selectedSlots.map(slot => ({
        date: slot.dateStr,
        startTime: slot.slotStart,
        endTime: slot.slotEnd
      }));

      // Get provider info from published template
      const providerId = publishedTemplate.userId;
      const providerPhone = publishedTemplate.spPhoneId;

      if (!providerId || !providerPhone) {
        throw new Error('Instructor information is missing');
      }

      console.log('Booking slots:', { slots, providerId, providerPhone });

      // Call booking API
      const response = await bookingAPI.createMultipleBookings(
        { slots, providerId, providerPhone },
        getAccessTokenSilently
      );

      // Check if response has data (success or failure)
      if (response.data) {
        const { successCount, failCount, failed } = response.data;
        
        if (failCount === 0) {
          // All bookings succeeded
          toast.success(`Successfully booked ${successCount} slot${successCount > 1 ? 's' : ''}!`);
          setSelectedSlots([]);
          await loadExistingBookings(publishedTemplate.spPhoneId);
        } else if (successCount > 0 && failCount > 0) {
          // Partial success
          // Get unique error messages to avoid duplication
          const uniqueErrors = [...new Set(failed.map(f => f.error))];
          const errorSummary = uniqueErrors.length === 1 && failCount > 1
            ? `${uniqueErrors[0]} (${failCount} slots)`
            : uniqueErrors.join(', ');
          
          toast.success(
            `Booked ${successCount} slot${successCount > 1 ? 's' : ''}. ` +
            `${failCount} slot${failCount > 1 ? 's' : ''} failed: ${errorSummary}`
          );

          // Keep only failed slots selected
          const failedKeys = failed.map(f =>
            `${f.booking.date}-${f.booking.startTime}-${f.booking.endTime}`
          );
          setSelectedSlots(prev =>
            prev.filter(slot => failedKeys.includes(slot.key))
          );
          await loadExistingBookings(publishedTemplate.spPhoneId);
        } else if (failCount > 0) {
          // All bookings failed - show error messages from failed array
          // Get unique error messages to avoid duplication
          const uniqueErrors = [...new Set(failed.map(f => f.error))];
          
          // If all errors are the same, show it once with count
          if (uniqueErrors.length === 1) {
            const errorMsg = uniqueErrors[0];
            if (failCount > 1) {
              throw new Error(`${errorMsg} (${failCount} slots failed)`);
            } else {
              throw new Error(errorMsg);
            }
          } else {
            // Multiple different errors - list them all
            throw new Error(uniqueErrors.join('. '));
          }
        }
      } else {
        throw new Error(response.error || 'Booking failed');
      }
    } catch (err) {
      console.error('Booking error:', err);
      setError(err.message);  // Don't add "Booking failed:" prefix - message is already clear
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setError(null);
      }, 5000);
    } finally {
      setIsBooking(false);
    }
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  // FIXED: toISOString() is UTC — during BST midnight local = 23:00 UTC previous day
  const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const toggleSlotSelection = (date, slotStart, slotEnd) => {
    const slotKey = `${toLocalDateStr(date)}-${slotStart}-${slotEnd}`;
    const slotInfo = {
      key: slotKey,
      date: date,
      dateStr: toLocalDateStr(date),
      dayName: DAYS_OF_WEEK[date.getDay()],
      slotStart,
      slotEnd,
      displayDate: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      displayDay: DAYS_OF_WEEK[date.getDay()]
    };

    setSelectedSlots(prev => {
      const exists = prev.find(s => s.key === slotKey);
      if (exists) {
        return prev.filter(s => s.key !== slotKey);
      } else {
        return [...prev, slotInfo];
      }
    });
  };

  const isSlotSelected = (date, slotStart, slotEnd) => {
    const slotKey = `${toLocalDateStr(date)}-${slotStart}-${slotEnd}`;
    return selectedSlots.some(s => s.key === slotKey);
  };

  // Helper functions (same as before)
  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const isDateInRange = (date) => {
    if (!publishedTemplate) return false;
    const checkDate = new Date(date);
    const from = new Date(publishedTemplate.settings.dateFrom + 'T00:00:00');
    const to   = new Date(publishedTemplate.settings.dateTo   + 'T00:00:00');
    return checkDate >= from && checkDate <= to;
  };

  const isDayEnabled = (date) => {
    if (!publishedTemplate) return false;
    const dayName = DAYS_OF_WEEK[date.getDay()];
    const s = publishedTemplate.settings;
    if (Array.isArray(s.workingDays)) return s.workingDays.includes(dayName);
    // Legacy fallback
    const fromIndex = DAYS_OF_WEEK.indexOf(s.dayFrom);
    const toIndex   = DAYS_OF_WEEK.indexOf(s.dayTo);
    const dayIndex  = DAYS_OF_WEEK.indexOf(dayName);
    return fromIndex <= toIndex
      ? dayIndex >= fromIndex && dayIndex <= toIndex
      : dayIndex >= fromIndex || dayIndex <= toIndex;
  };

  const isDateBookable = (date) => {
    if (!publishedTemplate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const advanceDate = new Date(today);
    advanceDate.setDate(today.getDate() + publishedTemplate.settings.daysInAdvance);
    return checkDate >= today && checkDate <= advanceDate;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const isSlotBlocked = (date, slotStart, slotEnd) => {
    if (!publishedTemplate) return { blocked: false };
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin = timeToMinutes(slotEnd);
    
    const dateStr = toLocalDateStr(date);
    const privateAppointments = publishedTemplate.privateAppointments || [];
    
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        const aptStartMin = timeToMinutes(apt.timeFrom);
        const aptEndMin = timeToMinutes(apt.timeTo);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    
    return { blocked: false };
  };

  /**
   * Check if a slot is already booked
   */
  const isSlotBooked = (date, slotStart, slotEnd) => {
    const dateStr = toLocalDateStr(date);
    const slotKey = `${dateStr}-${slotStart}-${slotEnd}`;
    return existingBookings.has(slotKey);
  };


  // ── Exceptional slot helpers ───────────────────────────────────────────────
  const getExceptionalEntry = (date) => {
    if (!publishedTemplate) return null;
    const dateStr = toLocalDateStr(date);
    return (publishedTemplate.exceptionalSlots || []).find(es => es.date === dateStr) || null;
  };

  const isWindowBooked = (date, win) => {
    const key = `${toLocalDateStr(date)}-${win.startTime}-${win.endTime}`;
    return existingBookings.has(key);
  };

  const isWindowSelected = (date, win) => {
    const key = `${toLocalDateStr(date)}-${win.startTime}-${win.endTime}`;
    return selectedSlots.some(s => s.key === key);
  };

  const toggleWindowSelection = (date, win) => {
    const dateStr = toLocalDateStr(date);
    const key     = `${dateStr}-${win.startTime}-${win.endTime}`;
    const slotInfo = {
      key,
      date,
      dateStr,
      dayName:      DAYS_OF_WEEK[date.getDay()],
      slotStart:    win.startTime,
      slotEnd:      win.endTime,
      displayDate:  date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      displayDay:   DAYS_OF_WEEK[date.getDay()],
      isExceptional: true
    };
    setSelectedSlots(prev => {
      const exists = prev.find(s => s.key === key);
      return exists ? prev.filter(s => s.key !== key) : [...prev, slotInfo];
    });
  };

  const countWindowSlots = (windows) => (windows || []).length;

  const fmtExcDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const generateTimeSlots = () => {
    if (!publishedTemplate) return [];
    
    const slots = [];
    const settings = publishedTemplate.settings;
    const [startHour, startMin] = settings.startTime.split(':').map(Number);
    const [endHour, endMin] = settings.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const slotDuration = settings.slotDuration;
    const breakDuration = settings.breakDuration;
    
    const hasMyBreak = settings.myBreakFrom && settings.myBreakTo;
    const myBreakStartMin = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
    const myBreakEndMin = hasMyBreak ? timeToMinutes(settings.myBreakTo) : null;
    
    let currentMinutes = startMinutes;
    let breakInserted = false;
    
    while (currentMinutes + slotDuration <= endMinutes) {
      if (hasMyBreak && !breakInserted && currentMinutes + slotDuration > myBreakStartMin) {
        if (currentMinutes < myBreakStartMin) {
          const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
          const slotEnd = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
          
          if (myBreakStartMin - currentMinutes >= slotDuration) {
            slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
          }
        }
        
        const breakStart = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
        const breakEnd = `${Math.floor(myBreakEndMin / 60).toString().padStart(2, '0')}:${(myBreakEndMin % 60).toString().padStart(2, '0')}`;
        slots.push({ start: breakStart, end: breakEnd, type: 'break' });
        
        currentMinutes = myBreakEndMin;
        breakInserted = true;
        continue;
      }
      
      const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
      const slotEndMinutes = currentMinutes + slotDuration;
      const slotEnd = `${Math.floor(slotEndMinutes / 60).toString().padStart(2, '0')}:${(slotEndMinutes % 60).toString().padStart(2, '0')}`;
      
      slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
      currentMinutes = slotEndMinutes + breakDuration;
    }
    
    return slots;
  };

  const formatTime12hr = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const formatTimeRange = (start, end) => {
    const [startHour] = start.split(':').map(Number);
    const [endHour] = end.split(':').map(Number);
    return `${startHour.toString().padStart(2, '0')}-${endHour.toString().padStart(2, '0')}`;
  };

  const weekDates = getWeekDates(currentWeekStart);
  const timeSlots = generateTimeSlots();

  if (!isServiceUser) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to students</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="booking-container">
        <div className="loading-state">
          <Clock size={48} className="loading-icon" />
          <h2>Loading your instructor's schedule...</h2>
        </div>
      </div>
    );
  }

  if (error && !publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Instructor Assigned. Request your instructor to Activate your account.</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (!publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Schedule Available</h2>
          <p className="error-message">Contact your Service Provider / Instructor to book a slot.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      {/* Messages shown via fixed-position toast — see src/utils/toast.js */}

      {/* Instructor Info 
      <div className="instructor-info">
        <h3>Instructor: {publishedTemplate.templateName || 'Your Instructor'}</h3>
      </div>
*/}
      {/* Slots Grid */}
      <div className="slots-preview-card">
        <div className="preview-header">
          <h2 className="preview-title">Weekly Schedule</h2>
          <div className="week-navigation">
            <button onClick={() => navigateWeek(-1)} className="nav-button">
              <ChevronLeft size={24} />
            </button>
            <span className="week-label">
              {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={() => navigateWeek(1)} className="nav-button">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-wrapper">
            <div className="grid-header">
              <div className="time-header">Time</div>
              {weekDates.map((date, idx) => {
                const excEntry    = getExceptionalEntry(date);
                const isExc       = !!excEntry;
                const isInRange   = isDateInRange(date);
                const isDayActive = isDayEnabled(date);
                const isBookable  = isDateBookable(date);
                const slotCount   = isExc ? (excEntry.windows || []).length : 0;

                if (isExc) {
                  return (
                    <div key={idx} className="day-header day-header-exceptional">
                      <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                      <div className="day-date day-date-exceptional">{date.getDate()}</div>
                      <div className="day-status day-status-exceptional">
                        <Sparkles size={10} /> Exc. ({slotCount})
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                    <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                    <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                    {isInRange && isDayActive && (
                      <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                        {isBookable ? '✓ Open' : 'Future'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="slots-container">
              {timeSlots.map((slot, slotIdx) => (
                <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                  <div className="time-cell">
                    <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                    <div className="time-range">
                      <div className="time-start">{formatTime12hr(slot.start)}</div>
                      <div className="time-end">{formatTime12hr(slot.end)}</div>
                    </div>
                  </div>
                  {slot.type === 'break' ? (
                    <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                      <div className="break-label">
                        <Coffee size={16} />
                        <span>BREAK TIME</span>
                      </div>
                    </div>
                  ) : (
                    weekDates.map((date, dateIdx) => {
                      const excEntry    = getExceptionalEntry(date);
                      const isExc       = !!excEntry;
                      const isInRange   = isDateInRange(date);
                      const isDayActive = isDayEnabled(date);
                      const isBookable  = isDateBookable(date);
                      const blockCheck  = isSlotBlocked(date, slot.start, slot.end);
                      const isSelected  = isSlotSelected(date, slot.start, slot.end);
                      const isBooked    = isSlotBooked(date, slot.start, slot.end);

                      // ── Exceptional date — display only, book via summary pills ──
                      if (isExc) {
                        return (
                          <div key={dateIdx} className="slot-cell slot-exceptional">
                            <div className="slot-status">
                              <div className="status-exceptional"><Sparkles size={10} /> Exc.</div>
                            </div>
                          </div>
                        );
                      }

                      // ── Regular date ──────────────────────────────────────
                      const isAvailable = isInRange && isDayActive && isBookable;
                      return (
                        <div
                          key={dateIdx}
                          className={`slot-cell ${
                            !isAvailable       ? 'slot-unavailable' :
                            blockCheck.blocked ? 'slot-blocked'     :
                            isBooked           ? 'slot-booked'      :
                            isSelected         ? 'slot-selected'    :
                            'slot-available'
                          } ${isAvailable && !blockCheck.blocked && !isBooked ? 'slot-clickable' : ''}`}
                          onClick={() => {
                            if (isAvailable && !blockCheck.blocked && !isBooked) {
                              toggleSlotSelection(date, slot.start, slot.end);
                            }
                          }}
                        >
                          <div className="slot-status">
                            {!isAvailable ? (
                              <div className="status-unavailable">-</div>
                            ) : blockCheck.blocked ? (
                              <div className="status-blocked">{blockCheck.reason}</div>
                            ) : isBooked ? (
                              <div className="status-booked">Booked<br />
                              From {formatTime12hr(slot.start)}
                              </div>
                            ) : (
                              <div className="status-available">
                                {isSelected && <span className="status-check">✓ </span>}
                                {formatTime12hr(slot.start)}<br />{formatTime12hr(slot.end)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            {timeSlots.length === 0 && (
              <div className="empty-state">
                <Clock size={48} className="empty-icon" />
                <p className="empty-text">No time slots available</p>
              </div>
            )}
          </div>
        </div>

        <div className="legend">
          <div className="legend-item">
            <div className="legend-box legend-box-available"></div>
            <span className="legend-text">Available to book</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-blocked"></div>
            <span className="legend-text">Blocked (Break/Private)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-locked"></div>
            <span className="legend-text">Future (not yet bookable)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-unavailable"></div>
            <span className="legend-text">Outside range/unavailable</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-exceptional"></div>
            <span className="legend-text">Exceptional slot</span>
          </div>
        </div>

        {/* Exceptional Slots — weekly summary with clickable window pills */}
        {(() => {
          const weekDateStrs = new Set(weekDates.map(d => toLocalDateStr(d)));
          const weekExcSlots = (publishedTemplate?.exceptionalSlots || [])
            .filter(es => weekDateStrs.has(es.date))
            .sort((a, b) => a.date.localeCompare(b.date));
          if (weekExcSlots.length === 0) return null;
          return (
            <div className="exc-summary-section" style={{ marginTop: '1rem' }}>
              <div className="exc-summary-header">
                <Sparkles size={15} className="exc-summary-icon" />
                <span className="exc-summary-title">Exceptional Slots This Week</span>
                <span className="exc-summary-count">
                  {weekExcSlots.length} date{weekExcSlots.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="exc-summary-rows">
                {weekExcSlots.map((es) => {
                  const excDate        = new Date(es.date + 'T00:00:00');
                  const isBookableDate = isDateBookable(excDate);
                  return (
                    <div key={es.date} className="exc-summary-row">
                      <span className="exc-row-date">{fmtExcDate(es.date)}</span>
                      <span className="exc-row-windows">
                        {(es.windows || [])
                          .slice()
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((w, i) => {
                            const booked   = isWindowBooked(excDate, w);
                            const selected = isWindowSelected(excDate, w);
                            const canBook  = isBookableDate && !booked;
                            return (
                              <span
                                key={i}
                                className={`exc-row-win ${
                                  booked   ? 'exc-win-booked'   :
                                  selected ? 'exc-win-selected' :
                                  canBook  ? 'exc-win-bookable' : 'exc-win-future'
                                }`}
                                onClick={() => { if (canBook) toggleWindowSelection(excDate, w); }}
                                title={
                                  booked   ? 'Already booked'     :
                                  !canBook ? 'Not yet bookable'   :
                                  selected ? 'Click to deselect'  : 'Click to select'
                                }
                              >
                                {booked ? '✓ ' : ''}{selected ? '✓ ' : ''}
                                {formatTime12hr(w.startTime)}–{formatTime12hr(w.endTime)}
                              </span>
                            );
                          })}
                      </span>
                      <span className="exc-row-slots">
                        {countWindowSlots(es.windows)} slot{countWindowSlots(es.windows) !== 1 ? 's' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Selected Slots */}
        {selectedSlots.length > 0 && (
          <div className="selected-slots-section">
            <div className="selected-slots-list">
              {selectedSlots.map((slot, idx) => (
                <div key={idx} className="selected-slot-item">
                  {slot.displayDate} – {slot.displayDay}
                  <br />
                  {formatTimeRange(slot.slotStart, slot.slotEnd)}
                </div>
              ))}
            </div>
            <button 
              className="btn-book-now"
              onClick={handleBookNow}
              disabled={isBooking}
            >
              {isBooking ? 'BOOKING...' : 'BOOK NOW'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}